import type { Prisma, PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { accountBalances } from '../accounting/posting.service.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import { csvLine } from '../csv/csv.parser.js';
import { ageBucket, type AgeBucket } from '../sales/calc.js';
import { isoOf, toDate } from '../sales/documents.service.js';
import {
  createExpense,
  createPayout,
  expenseInputSchema,
  openPayables,
  payoutInputSchema,
  voidExpense,
  voidPayout,
} from './payments.service.js';
import {
  approveBill,
  approveOrder,
  assertApprovalLimit,
  billInclude,
  billInputSchema,
  categoryView,
  createCategory,
  dec,
  ensureExpenseCategories,
  loadBill,
  loadOrder,
  orderInclude,
  orderInputSchema,
  orderToBill,
  outstandingOf,
  receiveOrder,
  saveBillDraft,
  saveOrderDraft,
  voidBill,
  type BillWithLines,
  type OrderWithLines,
} from './purchases.service.js';
import { logEvent } from '../sales/documents.service.js';
import { mountPurchaseSendRoutes } from './purchases.send.routes.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const OPEN_BILL = ['approved', 'partially_paid'];

export function orderView(o: OrderWithLines, partyName?: string) {
  return {
    id: o.id,
    number: o.number,
    status: o.status,
    partyId: o.partyId,
    partyName,
    currency: o.currency,
    exchangeRate: o.exchangeRate.toString(),
    issueDate: isoOf(o.issueDate),
    expectedDate: isoOf(o.expectedDate),
    reference: o.reference,
    notes: o.notes,
    terms: o.terms,
    subtotal: o.subtotal.toFixed(4),
    discountTotal: o.discountTotal.toFixed(4),
    taxTotal: o.taxTotal.toFixed(4),
    total: o.total.toFixed(4),
    baseTotal: o.baseTotal.toFixed(4),
    sentAt: o.sentAt,
    cancelReason: o.cancelReason,
    createdAt: o.createdAt,
    lines: o.lines.map((l) => ({
      id: l.id,
      lineNo: l.lineNo,
      itemId: l.itemId,
      categoryId: l.categoryId,
      description: l.description,
      quantity: l.quantity.toString(),
      receivedQty: l.receivedQty.toString(),
      billedQty: l.billedQty.toString(),
      unit: l.unit,
      unitPrice: l.unitPrice.toString(),
      discountType: l.discountType,
      discountValue: l.discountValue.toString(),
      vatCode: l.vatCode,
      vatRate: l.vatRate.toString(),
      lineNet: l.lineNet.toFixed(4),
      lineTax: l.lineTax.toFixed(4),
      lineTotal: l.lineTotal.toFixed(4),
    })),
  };
}

export function billView(b: BillWithLines, partyName?: string) {
  const today = toDate(new Date().toISOString().slice(0, 10));
  const owed = outstandingOf(b);
  const overdue = OPEN_BILL.includes(b.status) && !!b.dueDate && b.dueDate < today && owed.gt(0);
  return {
    id: b.id,
    number: b.number,
    vendorRef: b.vendorRef,
    status: b.status,
    displayStatus: overdue ? 'overdue' : b.status,
    overdue,
    partyId: b.partyId,
    partyName,
    orderId: b.orderId,
    currency: b.currency,
    exchangeRate: b.exchangeRate.toString(),
    issueDate: isoOf(b.issueDate),
    dueDate: isoOf(b.dueDate),
    notes: b.notes,
    whtApplicable: b.whtApplicable,
    subtotal: b.subtotal.toFixed(4),
    discountTotal: b.discountTotal.toFixed(4),
    taxTotal: b.taxTotal.toFixed(4),
    total: b.total.toFixed(4),
    baseTotal: b.baseTotal.toFixed(4),
    amountPaid: b.amountPaid.toFixed(4),
    outstanding: owed.toFixed(4),
    voidReason: b.voidReason,
    createdAt: b.createdAt,
    lines: b.lines.map((l) => ({
      id: l.id,
      lineNo: l.lineNo,
      orderLineId: l.orderLineId,
      itemId: l.itemId,
      categoryId: l.categoryId,
      description: l.description,
      quantity: l.quantity.toString(),
      unit: l.unit,
      unitPrice: l.unitPrice.toString(),
      discountType: l.discountType,
      discountValue: l.discountValue.toString(),
      vatCode: l.vatCode,
      vatRate: l.vatRate.toString(),
      lineNet: l.lineNet.toFixed(4),
      lineTax: l.lineTax.toFixed(4),
      lineTotal: l.lineTotal.toFixed(4),
    })),
  };
}

export function createPurchasesRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/purchases', authenticate({ db, config }), idempotent(db));

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });
  const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);
  const need = (req: Request, p: string) => {
    if (!can(req, p))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };
  const ownerScope = (req: Request) =>
    req.auth!.scope === 'own' ? { ownerId: req.auth!.userId } : {};
  const names = async (ids: number[]) => {
    const rows = await db.party.findMany({
      where: { id: { in: [...new Set(ids)] } },
      select: { id: true, name: true },
    });
    return new Map(rows.map((p) => [p.id, p.name]));
  };
  const vendorIdsLike = async (req: Request, search: string) =>
    (
      await db.party.findMany({
        where: { companyId: req.auth!.companyId, isVendor: true, name: { contains: search } },
        select: { id: true },
        take: 50,
      })
    ).map((p) => p.id);
  const events = async (type: string, id: number) => {
    const rows = await db.documentEvent.findMany({
      where: { entityType: type, entityId: id },
      orderBy: { id: 'asc' },
    });
    const users = await db.user.findMany({
      where: { id: { in: rows.map((e) => e.userId).filter((u): u is number => !!u) } },
      select: { id: true, name: true },
    });
    return rows.map((e) => ({
      id: e.id,
      type: e.type,
      note: e.note,
      at: e.createdAt,
      by: users.find((u) => u.id === e.userId)?.name ?? null,
    }));
  };
  const comms = (docType: string, id: number, companyId: number) =>
    db.communication.findMany({
      where: { companyId, docType, docId: id },
      orderBy: { id: 'desc' },
      omit: { bodyText: true },
      take: 50,
    });

  // ───────────── expense categories ─────────────
  const canCategory = (req: Request) =>
    can(req, 'purchases.expense.create') ||
    can(req, 'purchases.bill.create') ||
    can(req, 'purchases.order.create');
  const canSeeCategories = (req: Request) =>
    canCategory(req) ||
    can(req, 'purchases.expense.view') ||
    can(req, 'purchases.bill.view') ||
    can(req, 'purchases.order.view');

  router.get(
    '/purchases/categories',
    asyncHandler(async (req, res) => {
      if (!canSeeCategories(req)) need(req, 'purchases.bill.view');
      await ensureExpenseCategories(db, req.auth!.companyId);
      const rows = await db.expenseCategory.findMany({
        where: { companyId: req.auth!.companyId },
        orderBy: { name: 'asc' },
      });
      const accounts = await db.account.findMany({
        where: { id: { in: rows.map((r) => r.accountId) } },
        select: { id: true, code: true },
      });
      res.json({
        items: rows.map((r) => categoryView(r, accounts.find((a) => a.id === r.accountId)?.code)),
      });
    }),
  );
  router.get(
    '/purchases/categories/search',
    asyncHandler(async (req, res) => {
      if (!canSeeCategories(req)) need(req, 'purchases.bill.view');
      await ensureExpenseCategories(db, req.auth!.companyId);
      const q = String(req.query.q ?? '').trim();
      const rows = await db.expenseCategory.findMany({
        where: {
          companyId: req.auth!.companyId,
          active: true,
          ...(q ? { name: { contains: q } } : {}),
        },
        orderBy: { name: 'asc' },
        take: 20,
      });
      res.json({
        items: rows.map((c) => ({
          id: c.id,
          label: c.name,
          description: c.needsReview ? 'Incomplete' : undefined,
        })),
      });
    }),
  );
  router.post(
    '/purchases/categories',
    asyncHandler(async (req, res) => {
      if (!canCategory(req)) need(req, 'purchases.expense.create');
      const body = parse(
        z.object({
          name: z.string().trim().min(2).max(100),
          vatCode: z.string().trim().min(2).max(30).optional(),
        }),
        req.body,
      );
      await ensureExpenseCategories(db, req.auth!.companyId);
      const cat = await db.$transaction(async (tx) => {
        const c = await createCategory(tx, { ...actor(req), ...body });
        await audit(tx, actor(req), {
          action: 'expense_category.created',
          entity: 'expense_category',
          entityId: c.id,
          after: body,
        });
        return c;
      });
      res.status(201).json(categoryView(cat));
    }),
  );

  // ───────────── purchase orders ─────────────
  router.get(
    '/purchases/orders',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.view');
      const q = req.query as Record<string, string | undefined>;
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const search = q.search?.trim();
      const vids = search ? await vendorIdsLike(req, search) : [];
      const where: Prisma.PurchaseOrderWhereInput = {
        companyId: req.auth!.companyId,
        ...ownerScope(req),
        ...(q.status ? { status: q.status } : {}),
        ...(q.partyId ? { partyId: Number(q.partyId) } : {}),
        ...(search
          ? {
              OR: [
                { number: { contains: search } },
                { reference: { contains: search } },
                ...(vids.length ? [{ partyId: { in: vids } }] : []),
              ],
            }
          : {}),
      };
      const [total, rows] = await Promise.all([
        db.purchaseOrder.count({ where }),
        db.purchaseOrder.findMany({
          where,
          include: orderInclude,
          orderBy: { id: 'desc' },
          skip,
          take,
        }),
      ]);
      const n = await names(rows.map((r) => r.partyId));
      res.json({
        items: rows.map((r) => ({ ...orderView(r, n.get(r.partyId)), lines: undefined })),
        total,
        page,
        pageSize,
      });
    }),
  );

  router.get(
    '/purchases/orders/export.csv',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.export');
      const where: Prisma.PurchaseOrderWhereInput = {
        companyId: req.auth!.companyId,
        ...ownerScope(req),
      };
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader('Content-Disposition', 'attachment; filename="purchase-orders.csv"');
      res.write(
        '﻿' +
          csvLine([
            'number',
            'status',
            'vendor_name',
            'order_date',
            'expected_date',
            'currency',
            'exchange_rate',
            'reference',
            'notes',
            'terms',
            'line_description',
            'category',
            'quantity',
            'unit',
            'unit_price',
            'discount_percent',
            'vat_code',
            'order_total',
          ]) +
          '\r\n',
      );
      const rows = await db.purchaseOrder.findMany({
        where,
        include: orderInclude,
        orderBy: { id: 'asc' },
      });
      const n = await names(rows.map((r) => r.partyId));
      const cats = new Map(
        (await db.expenseCategory.findMany({ where: { companyId: req.auth!.companyId } })).map(
          (c) => [c.id, c.name],
        ),
      );
      let count = 0;
      for (const o of rows)
        for (const l of o.lines) {
          res.write(
            csvLine([
              o.number,
              o.status,
              n.get(o.partyId),
              isoOf(o.issueDate),
              isoOf(o.expectedDate),
              o.currency,
              o.exchangeRate.toString(),
              o.reference,
              o.notes,
              o.terms,
              l.description,
              l.categoryId ? cats.get(l.categoryId) : '',
              l.quantity.toString(),
              l.unit,
              l.unitPrice.toString(),
              l.discountType === 'percent' ? l.discountValue.toString() : '0',
              l.vatCode,
              o.total.toString(),
            ]) + '\r\n',
          );
          count += 1;
        }
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: 'purchase_orders',
          filters: req.query as Prisma.InputJsonValue,
          rowCount: count,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: 'purchase_orders',
        after: { rows: count },
      });
      res.end();
    }),
  );

  router.get(
    '/purchases/orders/:id',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.view');
      const o = await loadOrder(db, req.auth!, Number(req.params.id));
      const party = await db.party.findUnique({
        where: { id: o.partyId },
        include: { contacts: true },
      });
      const bills = await db.vendorBill.findMany({
        where: { orderId: o.id, status: { not: 'void' } },
        select: { id: true, number: true, status: true, total: true },
      });
      res.json({
        ...orderView(o, party?.name),
        party: party && {
          id: party.id,
          name: party.name,
          tin: party.tin,
          contacts: party.contacts.map((c) => ({
            id: c.id,
            name: c.name,
            email: c.email,
            phone: c.phone,
          })),
        },
        bills: bills.map((b) => ({ ...b, total: b.total.toFixed(4) })),
        events: await events('purchase_order', o.id),
        communications: await comms('purchaseorder', o.id, o.companyId),
      });
    }),
  );

  router.post(
    '/purchases/orders',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.create');
      const input = parse(orderInputSchema, req.body);
      const o = await db.$transaction(async (tx) => {
        const created = await saveOrderDraft(tx, { auth: req.auth!, input });
        await audit(tx, actor(req), {
          action: 'purchase_order.created',
          entity: 'purchase_order',
          entityId: created.id,
          after: { total: created.total.toString() },
        });
        return created;
      });
      res.status(201).json(orderView(o));
    }),
  );
  router.put(
    '/purchases/orders/:id',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.edit');
      const input = parse(orderInputSchema, req.body);
      const o = await db.$transaction(async (tx) => {
        const before = await loadOrder(tx, req.auth!, Number(req.params.id));
        const saved = await saveOrderDraft(tx, { auth: req.auth!, id: before.id, input });
        await audit(tx, actor(req), {
          action: 'purchase_order.updated',
          entity: 'purchase_order',
          entityId: saved.id,
          before: { total: before.total.toString() },
          after: { total: saved.total.toString() },
        });
        return saved;
      });
      res.json(orderView(o));
    }),
  );
  router.post(
    '/purchases/orders/:id/submit',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.edit');
      const o = await loadOrder(db, req.auth!, Number(req.params.id));
      if (o.status !== 'draft')
        throw new HttpError(409, 'INVALID_STATE', 'Only drafts can be submitted for approval');
      const updated = await db.purchaseOrder.update({
        where: { id: o.id },
        data: { status: 'pending_approval', submittedAt: new Date() },
        include: orderInclude,
      });
      await logEvent(db, {
        companyId: o.companyId,
        entityType: 'purchase_order',
        entityId: o.id,
        type: 'submitted',
        userId: req.auth!.userId,
      });
      res.json(orderView(updated));
    }),
  );
  router.post(
    '/purchases/orders/:id/approve',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.approve');
      const o = await loadOrder(db, req.auth!, Number(req.params.id));
      assertApprovalLimit(req.auth!, o.baseTotal);
      const approved = await db.$transaction(async (tx) => {
        const a = await approveOrder(tx, req.auth!, o.id);
        await audit(tx, actor(req), {
          action: 'purchase_order.approved',
          entity: 'purchase_order',
          entityId: o.id,
          after: { number: a.number },
        });
        return a;
      });
      res.json(orderView(approved));
    }),
  );
  router.post(
    '/purchases/orders/:id/reject',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.approve');
      const { reason } = parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      const o = await loadOrder(db, req.auth!, Number(req.params.id));
      if (o.status !== 'pending_approval')
        throw new HttpError(
          409,
          'INVALID_STATE',
          'Only orders waiting for approval can be sent back',
        );
      const updated = await db.purchaseOrder.update({
        where: { id: o.id },
        data: { status: 'draft' },
        include: orderInclude,
      });
      await logEvent(db, {
        companyId: o.companyId,
        entityType: 'purchase_order',
        entityId: o.id,
        type: 'rejected',
        userId: req.auth!.userId,
        note: reason,
      });
      res.json(orderView(updated));
    }),
  );
  router.post(
    '/purchases/orders/:id/receive',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.edit');
      const body = parse(
        z.object({
          lines: z
            .array(
              z.object({
                lineId: z.number().int().positive(),
                quantity: z.string().regex(/^\d{1,11}(\.\d{1,4})?$/),
              }),
            )
            .min(1)
            .max(200),
        }),
        req.body,
      );
      const o = await db.$transaction(async (tx) => {
        const r = await receiveOrder(tx, req.auth!, Number(req.params.id), body.lines);
        await audit(tx, actor(req), {
          action: 'purchase_order.received',
          entity: 'purchase_order',
          entityId: r.id,
          after: { status: r.status },
        });
        return r;
      });
      res.json(orderView(o));
    }),
  );
  router.post(
    '/purchases/orders/:id/close',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.edit');
      const o = await loadOrder(db, req.auth!, Number(req.params.id));
      if (!['partially_received', 'received', 'sent', 'approved'].includes(o.status))
        throw new HttpError(409, 'INVALID_STATE', 'Only approved orders can be closed');
      const updated = await db.purchaseOrder.update({
        where: { id: o.id },
        data: { status: 'closed' },
        include: orderInclude,
      });
      await logEvent(db, {
        companyId: o.companyId,
        entityType: 'purchase_order',
        entityId: o.id,
        type: 'closed',
        userId: req.auth!.userId,
      });
      res.json(orderView(updated));
    }),
  );
  router.post(
    '/purchases/orders/:id/cancel',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.delete');
      const { reason } = parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      const o = await loadOrder(db, req.auth!, Number(req.params.id));
      if (['cancelled', 'closed'].includes(o.status))
        throw new HttpError(409, 'INVALID_STATE', 'This order is already finished');
      if (o.lines.some((l) => dec(l.billedQty).gt(0)))
        throw new HttpError(
          409,
          'HAS_BILLS',
          'Part of this order has been billed. Void those bills first.',
        );
      const updated = await db.purchaseOrder.update({
        where: { id: o.id },
        data: { status: 'cancelled', cancelledAt: new Date(), cancelReason: reason },
        include: orderInclude,
      });
      await audit(db, actor(req), {
        action: 'purchase_order.cancelled',
        entity: 'purchase_order',
        entityId: o.id,
        after: { reason },
      });
      await logEvent(db, {
        companyId: o.companyId,
        entityType: 'purchase_order',
        entityId: o.id,
        type: 'cancelled',
        userId: req.auth!.userId,
        note: reason,
      });
      res.json(orderView(updated));
    }),
  );
  router.post(
    '/purchases/orders/:id/duplicate',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.order.create');
      const src = await loadOrder(db, req.auth!, Number(req.params.id));
      const copy = await db.$transaction((tx) =>
        saveOrderDraft(tx, {
          auth: req.auth!,
          input: {
            partyId: src.partyId,
            currency: src.currency,
            exchangeRate: src.exchangeRate.toString(),
            issueDate: new Date().toISOString().slice(0, 10),
            reference: src.reference,
            notes: src.notes,
            terms: src.terms,
            lines: src.lines.map((l) => ({
              itemId: l.itemId,
              categoryId: l.categoryId,
              description: l.description,
              quantity: l.quantity.toString(),
              unit: l.unit,
              unitPrice: l.unitPrice.toString(),
              discountType: l.discountType as 'none' | 'percent' | 'amount',
              discountValue: l.discountValue.toString(),
              vatCode: l.vatCode,
            })),
          },
        }),
      );
      res.status(201).json(orderView(copy));
    }),
  );
  router.post(
    '/purchases/orders/:id/convert-bill',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.bill.create');
      need(req, 'purchases.order.view');
      const body = parse(
        z.object({
          lines: z
            .array(
              z.object({
                lineId: z.number().int().positive(),
                quantity: z.string().regex(/^\d{1,11}(\.\d{1,4})?$/),
              }),
            )
            .max(200)
            .optional(),
        }),
        req.body,
      );
      const bill = await db.$transaction(async (tx) => {
        const b = await orderToBill(tx, req.auth!, Number(req.params.id), body.lines);
        await audit(tx, actor(req), {
          action: 'purchase_order.converted',
          entity: 'purchase_order',
          entityId: Number(req.params.id),
          after: { billId: b.id },
        });
        return b;
      });
      res.status(201).json(billView(bill));
    }),
  );

  // ───────────── vendor bills ─────────────
  router.get(
    '/purchases/bills',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.bill.view');
      const q = req.query as Record<string, string | undefined>;
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const search = q.search?.trim();
      const vids = search ? await vendorIdsLike(req, search) : [];
      const today = toDate(new Date().toISOString().slice(0, 10));
      const where: Prisma.VendorBillWhereInput = {
        AND: [
          { companyId: req.auth!.companyId, ...ownerScope(req) },
          q.status === 'overdue'
            ? { status: { in: OPEN_BILL }, dueDate: { lt: today } }
            : q.status
              ? { status: q.status }
              : {},
          q.open === 'true' ? { status: { in: OPEN_BILL } } : {},
          q.partyId ? { partyId: Number(q.partyId) } : {},
          search
            ? {
                OR: [
                  { number: { contains: search } },
                  { vendorRef: { contains: search } },
                  ...(vids.length ? [{ partyId: { in: vids } }] : []),
                ],
              }
            : {},
        ],
      };
      const [total, rows, agg] = await Promise.all([
        db.vendorBill.count({ where }),
        db.vendorBill.findMany({
          where,
          include: billInclude,
          orderBy: { id: 'desc' },
          skip,
          take,
        }),
        db.vendorBill.aggregate({ where, _sum: { total: true, amountPaid: true } }),
      ]);
      const n = await names(rows.map((r) => r.partyId));
      res.json({
        items: rows.map((r) => ({ ...billView(r, n.get(r.partyId)), lines: undefined })),
        total,
        page,
        pageSize,
        totals: {
          amount: dec(agg._sum.total ?? 0).toFixed(4),
          outstanding: dec(agg._sum.total ?? 0)
            .minus(dec(agg._sum.amountPaid ?? 0))
            .toFixed(4),
        },
      });
    }),
  );
  router.get(
    '/purchases/bills/export.csv',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.bill.export');
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader('Content-Disposition', 'attachment; filename="vendor-bills.csv"');
      res.write(
        '﻿' +
          csvLine([
            'number',
            'vendor_bill_number',
            'status',
            'vendor_name',
            'bill_date',
            'due_date',
            'currency',
            'exchange_rate',
            'line_description',
            'category',
            'quantity',
            'unit_price',
            'vat_code',
            'bill_total',
            'amount_paid',
          ]) +
          '\r\n',
      );
      const rows = await db.vendorBill.findMany({
        where: { companyId: req.auth!.companyId, ...ownerScope(req) },
        include: billInclude,
        orderBy: { id: 'asc' },
      });
      const n = await names(rows.map((r) => r.partyId));
      const cats = new Map(
        (await db.expenseCategory.findMany({ where: { companyId: req.auth!.companyId } })).map(
          (c) => [c.id, c.name],
        ),
      );
      let count = 0;
      for (const b of rows)
        for (const l of b.lines) {
          res.write(
            csvLine([
              b.number,
              b.vendorRef,
              b.status,
              n.get(b.partyId),
              isoOf(b.issueDate),
              isoOf(b.dueDate),
              b.currency,
              b.exchangeRate.toString(),
              l.description,
              cats.get(l.categoryId),
              l.quantity.toString(),
              l.unitPrice.toString(),
              l.vatCode,
              b.total.toString(),
              b.amountPaid.toString(),
            ]) + '\r\n',
          );
          count += 1;
        }
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: 'vendor_bills',
          filters: req.query as Prisma.InputJsonValue,
          rowCount: count,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: 'vendor_bills',
        after: { rows: count },
      });
      res.end();
    }),
  );
  router.get(
    '/purchases/bills/:id',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.bill.view');
      const b = await loadBill(db, req.auth!, Number(req.params.id));
      const party = await db.party.findUnique({ where: { id: b.partyId } });
      const order = b.orderId
        ? await db.purchaseOrder.findUnique({
            where: { id: b.orderId },
            select: { id: true, number: true },
          })
        : null;
      const allocations = await db.payoutAllocation.findMany({
        where: { targetType: 'bill', targetId: b.id, payout: { status: 'posted' } },
        include: { payout: true },
      });
      res.json({
        ...billView(b, party?.name),
        party: party && {
          id: party.id,
          name: party.name,
          tin: party.tin,
          whtApplicable: party.whtApplicable,
          paymentDetails: party.paymentDetails ? 'On file' : null,
        },
        order,
        payments: allocations.map((a) => ({
          payoutId: a.payoutId,
          payoutNo: a.payout.payoutNo,
          date: isoOf(a.payout.payoutDate),
          amount: a.amount.toFixed(4),
        })),
        events: await events('vendor_bill', b.id),
      });
    }),
  );
  router.post(
    '/purchases/bills',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.bill.create');
      const input = parse(billInputSchema, req.body);
      const b = await db.$transaction(async (tx) => {
        const created = await saveBillDraft(tx, { auth: req.auth!, input });
        await audit(tx, actor(req), {
          action: 'vendor_bill.created',
          entity: 'vendor_bill',
          entityId: created.id,
          after: { total: created.total.toString() },
        });
        return created;
      });
      res.status(201).json(billView(b));
    }),
  );
  router.put(
    '/purchases/bills/:id',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.bill.edit');
      const input = parse(billInputSchema, req.body);
      const b = await db.$transaction(async (tx) => {
        const before = await loadBill(tx, req.auth!, Number(req.params.id));
        const saved = await saveBillDraft(tx, { auth: req.auth!, id: before.id, input });
        await audit(tx, actor(req), {
          action: 'vendor_bill.updated',
          entity: 'vendor_bill',
          entityId: saved.id,
          before: { total: before.total.toString() },
          after: { total: saved.total.toString() },
        });
        return saved;
      });
      res.json(billView(b));
    }),
  );
  router.post(
    '/purchases/bills/:id/approve',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.bill.approve');
      const b0 = await loadBill(db, req.auth!, Number(req.params.id));
      assertApprovalLimit(req.auth!, b0.baseTotal);
      const b = await db.$transaction(async (tx) => {
        const a = await approveBill(tx, req.auth!, b0.id);
        await audit(tx, actor(req), {
          action: 'vendor_bill.approved',
          entity: 'vendor_bill',
          entityId: b0.id,
          after: { number: a.number, total: a.total.toString() },
        });
        return a;
      });
      res.json(billView(b));
    }),
  );
  router.post(
    '/purchases/bills/:id/void',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.bill.delete');
      const { reason } = parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      const b = await db.$transaction(async (tx) => {
        const v = await voidBill(tx, req.auth!, Number(req.params.id), reason);
        await audit(tx, actor(req), {
          action: 'vendor_bill.voided',
          entity: 'vendor_bill',
          entityId: v.id,
          after: { reason },
        });
        return v;
      });
      res.json(billView(b));
    }),
  );

  // ───────────── expenses ─────────────
  const expenseView = (
    e: Prisma.ExpenseGetPayload<object>,
    extra: { categoryName?: string; partyName?: string; accountName?: string } = {},
  ) => ({
    id: e.id,
    number: e.number,
    categoryId: e.categoryId,
    categoryName: extra.categoryName,
    partyId: e.partyId,
    partyName: extra.partyName,
    expenseDate: isoOf(e.expenseDate),
    description: e.description,
    currency: e.currency,
    exchangeRate: e.exchangeRate.toString(),
    amount: e.amount.toFixed(4),
    vatCode: e.vatCode,
    vatAmount: e.vatAmount.toFixed(4),
    total: e.total.toFixed(4),
    paidBy: e.paidBy,
    financialAccountId: e.financialAccountId,
    accountName: extra.accountName,
    reference: e.reference,
    amountPaid: e.amountPaid.toFixed(4),
    outstanding:
      e.paidBy === 'staff' && e.status !== 'void' ? outstandingOf(e).toFixed(4) : '0.0000',
    status: e.status,
    voidReason: e.voidReason,
    createdAt: e.createdAt,
  });
  const decorateExpenses = async (rows: Prisma.ExpenseGetPayload<object>[], companyId: number) => {
    const cats = new Map(
      (await db.expenseCategory.findMany({ where: { companyId } })).map((c) => [c.id, c.name]),
    );
    const p = await names(rows.map((r) => r.partyId).filter((x): x is number => !!x));
    const accts = new Map(
      (
        await db.financialAccount.findMany({
          where: { companyId },
          select: { id: true, name: true },
        })
      ).map((a) => [a.id, a.name]),
    );
    return rows.map((e) =>
      expenseView(e, {
        categoryName: cats.get(e.categoryId),
        partyName: e.partyId ? p.get(e.partyId) : undefined,
        accountName: e.financialAccountId ? accts.get(e.financialAccountId) : undefined,
      }),
    );
  };
  const expenseScope = (req: Request): Prisma.ExpenseWhereInput => ({
    companyId: req.auth!.companyId,
    ...(req.auth!.scope === 'own' ? { createdBy: req.auth!.userId } : {}),
  });

  router.get(
    '/purchases/expenses',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.expense.view');
      const q = req.query as Record<string, string | undefined>;
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const search = q.search?.trim();
      const where: Prisma.ExpenseWhereInput = {
        ...expenseScope(req),
        ...(q.status ? { status: q.status } : {}),
        ...(q.categoryId ? { categoryId: Number(q.categoryId) } : {}),
        ...(q.paidBy ? { paidBy: q.paidBy } : {}),
        ...(search
          ? {
              OR: [
                { number: { contains: search } },
                { description: { contains: search } },
                { reference: { contains: search } },
              ],
            }
          : {}),
      };
      const [total, rows, agg] = await Promise.all([
        db.expense.count({ where }),
        db.expense.findMany({ where, orderBy: { id: 'desc' }, skip, take }),
        db.expense.aggregate({
          where: { ...where, status: { not: 'void' } },
          _sum: { baseTotal: true },
        }),
      ]);
      res.json({
        items: await decorateExpenses(rows, req.auth!.companyId),
        total,
        page,
        pageSize,
        totals: { base: dec(agg._sum.baseTotal ?? 0).toFixed(0) },
      });
    }),
  );
  router.get(
    '/purchases/expenses/export.csv',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.expense.export');
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader('Content-Disposition', 'attachment; filename="expenses.csv"');
      res.write(
        '﻿' +
          csvLine([
            'number',
            'status',
            'date',
            'category',
            'description',
            'vendor_or_staff',
            'paid_by',
            'paid_from',
            'currency',
            'amount',
            'vat_code',
            'vat',
            'total',
            'reference',
          ]) +
          '\r\n',
      );
      const rows = await db.expense.findMany({ where: expenseScope(req), orderBy: { id: 'asc' } });
      const view = await decorateExpenses(rows, req.auth!.companyId);
      for (const e of view)
        res.write(
          csvLine([
            e.number,
            e.status,
            e.expenseDate,
            e.categoryName,
            e.description,
            e.partyName,
            e.paidBy,
            e.accountName,
            e.currency,
            e.amount,
            e.vatCode,
            e.vatAmount,
            e.total,
            e.reference,
          ]) + '\r\n',
        );
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: 'expenses',
          filters: req.query as Prisma.InputJsonValue,
          rowCount: view.length,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: 'expenses',
        after: { rows: view.length },
      });
      res.end();
    }),
  );
  router.get(
    '/purchases/expenses/:id',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.expense.view');
      const e = await db.expense.findFirst({
        where: { id: Number(req.params.id), ...expenseScope(req) },
      });
      if (!e) throw new HttpError(404, 'NOT_FOUND', 'Expense not found');
      const [view] = await decorateExpenses([e], e.companyId);
      const allocations = await db.payoutAllocation.findMany({
        where: { targetType: 'expense', targetId: e.id, payout: { status: 'posted' } },
        include: { payout: true },
      });
      res.json({
        ...view,
        payments: allocations.map((a) => ({
          payoutId: a.payoutId,
          payoutNo: a.payout.payoutNo,
          date: isoOf(a.payout.payoutDate),
          amount: a.amount.toFixed(4),
        })),
        events: await events('expense', e.id),
      });
    }),
  );
  router.post(
    '/purchases/expenses',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.expense.create');
      const input = parse(expenseInputSchema, req.body);
      const e = await db.$transaction(async (tx) => {
        const created = await createExpense(tx, req.auth!, input);
        await audit(tx, actor(req), {
          action: 'expense.created',
          entity: 'expense',
          entityId: created.id,
          after: { number: created.number, total: created.total.toString() },
        });
        return created;
      });
      const [view] = await decorateExpenses([e], e.companyId);
      res.status(201).json(view);
    }),
  );
  router.post(
    '/purchases/expenses/:id/void',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.expense.delete');
      const { reason } = parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      const e = await db.$transaction(async (tx) => {
        const v = await voidExpense(tx, req.auth!, Number(req.params.id), reason);
        await audit(tx, actor(req), {
          action: 'expense.voided',
          entity: 'expense',
          entityId: v.id,
          after: { reason },
        });
        return v;
      });
      const [view] = await decorateExpenses([e], e.companyId);
      res.json(view);
    }),
  );

  // ───────────── payout receipts ─────────────
  const payoutView = async (
    p: Prisma.PayoutReceiptGetPayload<{ include: { allocations: true } }>,
  ) => {
    const party = await db.party.findUnique({ where: { id: p.partyId }, select: { name: true } });
    const account = await db.financialAccount.findUnique({
      where: { id: p.financialAccountId },
      select: { name: true },
    });
    const billIds = p.allocations.filter((a) => a.targetType === 'bill').map((a) => a.targetId);
    const expIds = p.allocations.filter((a) => a.targetType === 'expense').map((a) => a.targetId);
    const bills = billIds.length
      ? await db.vendorBill.findMany({
          where: { id: { in: billIds } },
          select: { id: true, number: true },
        })
      : [];
    const exps = expIds.length
      ? await db.expense.findMany({
          where: { id: { in: expIds } },
          select: { id: true, number: true },
        })
      : [];
    return {
      id: p.id,
      payoutNo: p.payoutNo,
      partyId: p.partyId,
      partyName: party?.name,
      payoutDate: isoOf(p.payoutDate),
      financialAccountId: p.financialAccountId,
      accountName: account?.name,
      currency: p.currency,
      exchangeRate: p.exchangeRate.toString(),
      amount: p.amount.toFixed(4),
      whtWithheld: p.whtWithheld.toFixed(4),
      reference: p.reference,
      notes: p.notes,
      allocatedAmount: p.allocatedAmount.toFixed(4),
      status: p.status,
      voidReason: p.voidReason,
      sentAt: p.sentAt,
      createdAt: p.createdAt,
      allocations: p.allocations.map((a) => ({
        id: a.id,
        targetType: a.targetType,
        targetId: a.targetId,
        label:
          (a.targetType === 'bill'
            ? bills.find((b) => b.id === a.targetId)?.number
            : exps.find((e) => e.id === a.targetId)?.number) ?? null,
        amount: a.amount.toFixed(4),
      })),
    };
  };
  const payoutScope = (req: Request): Prisma.PayoutReceiptWhereInput => ({
    companyId: req.auth!.companyId,
    ...(req.auth!.scope === 'own' ? { createdBy: req.auth!.userId } : {}),
  });

  router.get(
    '/purchases/payouts',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.payout.view');
      const q = req.query as Record<string, string | undefined>;
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const search = q.search?.trim();
      const vids = search ? await vendorIdsLike(req, search) : [];
      const where: Prisma.PayoutReceiptWhereInput = {
        ...payoutScope(req),
        ...(q.partyId ? { partyId: Number(q.partyId) } : {}),
        ...(q.status ? { status: q.status } : {}),
        ...(search
          ? {
              OR: [
                { payoutNo: { contains: search } },
                { reference: { contains: search } },
                ...(vids.length ? [{ partyId: { in: vids } }] : []),
              ],
            }
          : {}),
      };
      const [total, rows] = await Promise.all([
        db.payoutReceipt.count({ where }),
        db.payoutReceipt.findMany({
          where,
          include: { allocations: true },
          orderBy: { id: 'desc' },
          skip,
          take,
        }),
      ]);
      res.json({ items: await Promise.all(rows.map(payoutView)), total, page, pageSize });
    }),
  );
  router.get(
    '/purchases/payables',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.payout.create');
      const partyId = Number(req.query.partyId);
      if (!partyId) throw new HttpError(400, 'VALIDATION_ERROR', 'partyId is required');
      const currency = typeof req.query.currency === 'string' ? req.query.currency : undefined;
      const rows = await openPayables(db, req.auth!.companyId, partyId, currency);
      res.json({
        items: rows.map((p) => ({
          type: p.type,
          id: p.id,
          label: p.label,
          currency: p.currency,
          owed: p.owed.toFixed(4),
          date: isoOf(p.date),
        })),
      });
    }),
  );
  router.get(
    '/purchases/payout-accounts',
    asyncHandler(async (req, res) => {
      if (!can(req, 'purchases.payout.create') && !can(req, 'purchases.expense.create'))
        need(req, 'purchases.payout.create');
      const rows = await db.financialAccount.findMany({
        where: { companyId: req.auth!.companyId, status: 'active' },
        orderBy: [{ kind: 'asc' }, { name: 'asc' }],
        select: { id: true, name: true, kind: true, currency: true },
      });
      res.json({ items: rows });
    }),
  );
  router.get(
    '/purchases/payouts/export.csv',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.payout.export');
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader('Content-Disposition', 'attachment; filename="payouts.csv"');
      res.write(
        '﻿' +
          csvLine([
            'payout_number',
            'status',
            'vendor_name',
            'payout_date',
            'account',
            'amount',
            'wht_withheld',
            'currency',
            'exchange_rate',
            'reference',
            'bill_number',
            'allocated_amount',
          ]) +
          '\r\n',
      );
      const rows = await db.payoutReceipt.findMany({
        where: payoutScope(req),
        include: { allocations: true },
        orderBy: { id: 'asc' },
      });
      let count = 0;
      for (const r of rows) {
        const v = await payoutView(r);
        const lines = v.allocations.length ? v.allocations : [null];
        for (const a of lines) {
          res.write(
            csvLine([
              v.payoutNo,
              v.status,
              v.partyName,
              v.payoutDate,
              v.accountName,
              v.amount,
              v.whtWithheld,
              v.currency,
              v.exchangeRate,
              v.reference,
              a?.label ?? '',
              a?.amount ?? '',
            ]) + '\r\n',
          );
          count += 1;
        }
      }
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: 'payouts',
          filters: req.query as Prisma.InputJsonValue,
          rowCount: count,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: 'payouts',
        after: { rows: count },
      });
      res.end();
    }),
  );
  router.get(
    '/purchases/payouts/:id',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.payout.view');
      const p = await db.payoutReceipt.findFirst({
        where: { id: Number(req.params.id), ...payoutScope(req) },
        include: { allocations: true },
      });
      if (!p) throw new HttpError(404, 'NOT_FOUND', 'Payout not found');
      res.json({
        ...(await payoutView(p)),
        events: await events('payout', p.id),
        communications: await comms('payout', p.id, p.companyId),
      });
    }),
  );
  router.post(
    '/purchases/payouts',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.payout.create');
      const input = parse(payoutInputSchema, req.body);
      const p = await db.$transaction(async (tx) => {
        const created = await createPayout(tx, req.auth!, input);
        await audit(tx, actor(req), {
          action: 'payout.created',
          entity: 'payout',
          entityId: created.id,
          after: {
            payoutNo: created.payoutNo,
            amount: created.amount.toString(),
            wht: created.whtWithheld.toString(),
          },
        });
        return created;
      });
      const full = await db.payoutReceipt.findUniqueOrThrow({
        where: { id: p.id },
        include: { allocations: true },
      });
      res.status(201).json(await payoutView(full));
    }),
  );
  router.post(
    '/purchases/payouts/:id/void',
    asyncHandler(async (req, res) => {
      need(req, 'purchases.payout.delete');
      const { reason } = parse(z.object({ reason: z.string().trim().min(3).max(300) }), req.body);
      await db.$transaction(async (tx) => {
        const v = await voidPayout(tx, req.auth!, Number(req.params.id), reason);
        await audit(tx, actor(req), {
          action: 'payout.voided',
          entity: 'payout',
          entityId: v.id,
          after: { reason },
        });
      });
      const full = await db.payoutReceipt.findUniqueOrThrow({
        where: { id: Number(req.params.id) },
        include: { allocations: true },
      });
      res.json(await payoutView(full));
    }),
  );

  // ───────────── payables ageing (with control check against the AP ledger) ─────────────
  router.get(
    '/purchases/reports/ageing',
    asyncHandler(async (req, res) => {
      if (!can(req, 'accounting.report.view') && !can(req, 'purchases.bill.view'))
        need(req, 'purchases.bill.view');
      const asOfStr =
        typeof req.query.asOf === 'string' ? req.query.asOf : new Date().toISOString().slice(0, 10);
      const asOf = toDate(asOfStr);
      const bills = await db.vendorBill.findMany({
        where: { companyId: req.auth!.companyId, ...ownerScope(req), status: { in: OPEN_BILL } },
      });
      const expenses = await db.expense.findMany({
        where: {
          companyId: req.auth!.companyId,
          paidBy: 'staff',
          status: { in: ['posted', 'partially_paid'] },
          ...(req.auth!.scope === 'own' ? { createdBy: req.auth!.userId } : {}),
        },
      });
      const n = await names([
        ...bills.map((b) => b.partyId),
        ...expenses.map((e) => e.partyId!).filter(Boolean),
      ]);
      type Row = Record<AgeBucket | 'total', Decimal> & { partyName: string };
      const byParty = new Map<number, Row>();
      let subledger = new Decimal(0);
      const add = (partyId: number, due: Date, owed: Decimal, rate: Decimal) => {
        const base = owed.times(rate).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
        subledger = subledger.plus(base);
        const row =
          byParty.get(partyId) ??
          ({
            partyName: n.get(partyId) ?? '',
            current: new Decimal(0),
            d1_30: new Decimal(0),
            d31_60: new Decimal(0),
            d61_90: new Decimal(0),
            d90_plus: new Decimal(0),
            total: new Decimal(0),
          } as Row);
        const bucket = ageBucket(due, asOf);
        row[bucket] = row[bucket].plus(base);
        row.total = row.total.plus(base);
        byParty.set(partyId, row);
      };
      for (const b of bills)
        add(b.partyId, b.dueDate ?? b.issueDate, outstandingOf(b), dec(b.exchangeRate));
      for (const e of expenses)
        if (e.partyId) add(e.partyId, e.expenseDate, outstandingOf(e), dec(e.exchangeRate));
      const rows = [...byParty.entries()]
        .map(([partyId, r]) => ({
          partyId,
          partyName: r.partyName,
          current: r.current.toFixed(0),
          d1_30: r.d1_30.toFixed(0),
          d31_60: r.d31_60.toFixed(0),
          d61_90: r.d61_90.toFixed(0),
          d90_plus: r.d90_plus.toFixed(0),
          total: r.total.toFixed(0),
        }))
        .sort((a, b) => Number(b.total) - Number(a.total));
      const totals = rows.reduce(
        (t, r) => ({
          current: t.current + Number(r.current),
          d1_30: t.d1_30 + Number(r.d1_30),
          d31_60: t.d31_60 + Number(r.d31_60),
          d61_90: t.d61_90 + Number(r.d61_90),
          d90_plus: t.d90_plus + Number(r.d90_plus),
          total: t.total + Number(r.total),
        }),
        { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_plus: 0, total: 0 },
      );
      let apLedger: string | null = null;
      if (req.auth!.scope === 'all' && can(req, 'accounting.report.view')) {
        const ap = await db.account.findFirst({
          where: { companyId: req.auth!.companyId, systemKey: 'AP' },
        });
        if (ap) {
          const [b] = await accountBalances(db, req.auth!.companyId, { accountIds: [ap.id] });
          apLedger = (b ? b.credit.minus(b.debit) : new Decimal(0)).toFixed(0);
        }
      }
      res.json({
        asOf: asOfStr,
        rows,
        totals,
        subledger: subledger.toFixed(0),
        apLedger,
        reconciled: apLedger === null ? null : apLedger === subledger.toFixed(0),
      });
    }),
  );

  mountPurchaseSendRoutes(router, { db, config });
  return router;
}
